import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { Typography, Box, Button, Modal, Grid, TextField, Autocomplete } from '@mui/material';
import axios from 'axios';
import { BASE_URL } from '../../components/CommonExport';

const FantasyEleven = () => {
  const [fantasyData, setFantasyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replaceModal, setReplaceModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [replacementPlayer, setReplacementPlayer] = useState(null);
  const matches = useSelector((state) => state.matchReducer.matches);
  const matchId = matches.match_id;


  const fetchFantasyEleven = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/fantasy-eleven/get-fantasy-11/${matchId}`);
      console.log('fantasy eleven data', response.data.data)
      setFantasyData(response.data.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch fantasy eleven data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFantasyEleven();
  }, [matchId]);

  const handleReplacePlayer = async () => {
    if (!selectedPlayer || !replacementPlayer) {
      alert('Please select both players');
      return;
    }

    try {
      await axios.put(`${BASE_URL}/admin/fantasy-eleven/replace-player/${matchId}`, {
        player_id1: selectedPlayer?.player_id,
        player_id2: replacementPlayer?.player_id
      });
      
      // Refresh the fantasy eleven data
      await fetchFantasyEleven();
      
      setReplaceModal(false);
      setSelectedPlayer(null);
      setReplacementPlayer(null);
    } catch (err) {
      console.error('Error replacing player:', err);
      alert('Failed to replace player');
    }
  };

  const columns = [
    { field: 'player_name', headerName: 'Player Name', flex: 1 },
    {field: 'matches_played', headerName: 'Matches Played', type: 'number', flex: 1},
    { field: 'fantasy_score', headerName: 'Fantasy Score', type: 'number', flex: 1 },
    { field: 'trending_score', headerName: 'Trending Score', type: 'number', flex: 1 },
    { field: 'form_score', headerName: 'Form Score', type: 'number', flex: 1 },
    { field: 'type', headerName: 'Type', flex: 1, align: 'center', headerAlign: 'center' },
    { 
      field: 'playing11', 
      headerName: 'Playing 11', 
      type: 'boolean',
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: params.row.playing11 === true ? '#4CAF50' : '',
            margin: 'auto'
          }}
        />
      )
    },
    { 
      field: 'in_form', 
      headerName: 'In Form', 
      type: 'boolean',
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: params.row.in_form === true ? '#4CAF50' : '',
            margin: 'auto'
          }}
        />
      )
    },
    { 
      field: 'crowd_fav', 
      headerName: 'Crowd Favorite', 
      type: 'boolean',
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: params.row.crowd_fav === true ? '#4CAF50' : '',
            margin: 'auto'
          }}
        />
      )
    },
    { 
      field: 'power_hitter', 
      headerName: 'Power Hitter', 
      type: 'boolean',
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: params.row.power_hitter === true ? '#4CAF50' : '',
            margin: 'auto'
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        params.row.playing11 ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setSelectedPlayer(params.row);
              setReplaceModal(true);
            }}
          >
            Replace
          </Button>
        ) : null
      )
    }
  ];

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ height: 400, width: '100%', mt: 3 }}>
      <DataGrid
        rows={fantasyData}
        columns={columns}
        pageSize={5}
        rowsPerPageOptions={[5, 10, 20]}
        loading={loading}
        getRowId={(row) => row.player_id}
      />

      <Modal
        open={replaceModal}
        onClose={() => setReplaceModal(false)}
        aria-labelledby="replace-player-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '85%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2
        }}>
          <Typography variant="h6" component="h2" gutterBottom>
            Replace Player
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">
                Selected Player: {selectedPlayer?.player_name} ({selectedPlayer?.type})
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={fantasyData.filter(player => player.playing11 === false)}
                getOptionLabel={(option) => `${option.player_name} (${option.type}) (${option.fantasy_score}, ${option.form_score}, ${option.trending_score})`}
                value={replacementPlayer}
                onChange={(event, newValue) => setReplacementPlayer(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Replacement Player"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setReplaceModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleReplacePlayer}
                >
                  Replace
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Box>
  );
};

export default FantasyEleven;
